import { Injectable } from '@angular/core';
import { API as AWS_API } from 'aws-amplify';
import { forkJoin, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CONST_AWS_API, MERCH_API } from 'src/app/core/constants';
import { environment } from 'src/environments/environment';
import { AppPaymentTVService } from './app-payment-tv.service';

@Injectable({
  providedIn: 'root'
})
export class MerchCartService {

  constructor(private appPaymentService: AppPaymentTVService) { }

  removeItems(items: any[], clearAll: boolean = false): Observable<number[]> {
    const requests = clearAll
      ? [this.appPaymentService.postRequest(MERCH_API.CLEAR_CART, { cart_id: items[0].cart_id })]
      : items.map(item => this.appPaymentService.postRequest(MERCH_API.DELETE_CART_PRODUCT, { item_id: item.id }));
    return forkJoin(requests).pipe(map((responses: any[]) => {
      const failed = responses.find(response => response.status !== 0);
      if (failed) {
        throw failed.message || 'Unable to remove item from cart';
      }
      return items.map(item => item.id);
    }));
  }

  // Product page route for a cart line; carries the selected attribute value ids so the page reopens with them.
  overviewRoute(basePath: string, product: any): { commands: string[], extras: { queryParams: { attrs?: string } } } {
    return {
      commands: [`${basePath}/overviewProduct/${product.product_id}/${product.collection_id || 0}`],
      extras: { queryParams: this.overviewQueryParams(product.product_data) }
    };
  }

  private overviewQueryParams(productData: any): { attrs?: string } {
    const ids = (productData?.attributes || [])
      .map(attr => (attr.attributesList || []).find(att => att.is_selected)?.value_id)
      .filter(id => id != null);
    return ids.length ? { attrs: ids.join(',') } : {};
  }

  // Cart rows are only removed after every wishlist add succeeds, so nothing is lost on failure.
  moveToWishlist(items: any[], accessToken: string, clearAll: boolean = false): Observable<number[]> {
    const adds = items.map(item => this.postWishlist(accessToken, {
      type: 'add',
      product_id: item.product_id,
      collection_id: item.collection_id,
      organization_id: item.organization_id
    }));
    return from(Promise.all(adds)).pipe(
      map((responses: any[]) => {
        if (responses.some(response => response.data?.statusCode !== '200')) {
          throw 'Unable to move item to wishlist';
        }
      }),
      switchMap(() => this.removeItems(items, clearAll))
    );
  }

  // Best effort after a completed order: a failed delete must never surface to the buyer.
  removeFromWishlist(productIds: any[], accessToken: string): Promise<void> {
    const ids = Array.from(new Set((productIds || []).filter(id => id != null)));
    if (!accessToken || !ids.length) {
      return Promise.resolve();
    }
    return Promise.resolve()
      .then(() => Promise.all(ids.map(id => this.postWishlist(accessToken, { type: 'delete', product_id: id }).catch(() => null))))
      .then(() => undefined, () => undefined);
  }

  private postWishlist(accessToken: string, body: any): Promise<any> {
    return AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.WISH_LIST, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': environment.AWS_Cognito.X_Api_Key,
        'Authorization': `Bearer ${accessToken}`
      },
      response: true,
      body
    });
  }
}
